<script setup lang="ts">
import { useScrollLock } from '@shared/lib'

// Нижняя шторка стартового промпта (Dodo-стайл): затемнение, карточка с маскотом,
// два действия. Чистое представление — состояние и очерёдность живут в композаблах
// фич. Общая для «Укажите адрес для заказа» и «Выберите филиал»: они различались
// только текстами и иконкой, а ~70 строк SCSS были продублированы дословно.
const props = withDefaults(defineProps<{
  open: boolean
  /** Имя иконки UIcon для маскота */
  icon: string
  subtitle: string
  title: string
  primaryLabel: string
  secondaryLabel?: string
}>(), {
  secondaryLabel: 'Не сейчас',
})

const emit = defineEmits<{
  (e: 'primary'): void
  (e: 'dismiss'): void
}>()

// Пока шторка открыта — фон не скроллится и не «прыгает» (компенсация скроллбара).
useScrollLock(() => props.open)
</script>

<template>
  <Teleport to="body">
    <Transition name="start-prompt">
      <div
        v-if="open"
        class="start-prompt"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <!-- Затемнение: клик трактуем как «Не сейчас» -->
        <div class="start-prompt__backdrop" @click="emit('dismiss')" />

        <div class="start-prompt__sheet">
          <div class="start-prompt__handle" />

          <!-- Маскот: плейсхолдер, заменим на брендовый ассет -->
          <div class="start-prompt__mascot" aria-hidden="true">
            <UIcon :name="icon" class="h-12 w-12 text-orange-500" />
          </div>

          <p class="start-prompt__subtitle">{{ subtitle }}</p>
          <h3 class="start-prompt__title">{{ title }}</h3>

          <div class="start-prompt__actions">
            <UiButtonMain
              class="flex-1 !bg-gray-100 !text-gray-900 hover:!bg-gray-200 dark:!bg-gray-800 dark:!text-white dark:hover:!bg-gray-700"
              size="xl"
              color="red"
              block
              :label="secondaryLabel"
              @click="emit('dismiss')"
            />
            <UiButtonMain
              class="flex-1"
              size="xl"
              color="red"
              block
              :label="primaryLabel"
              @click="emit('primary')"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.start-prompt {
  @apply fixed inset-0 z-[60] flex items-end justify-center;

  &__backdrop {
    @apply absolute inset-0 bg-black/50;
  }

  &__sheet {
    @apply relative z-[1] w-full max-w-[640px] rounded-t-3xl bg-white px-5 pb-6 pt-3 text-center;
    @apply dark:bg-gray-900;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1.5rem);
  }

  &__handle {
    @apply mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-700;
  }

  &__mascot {
    @apply mx-auto mb-3 flex h-24 w-24 items-center justify-center rounded-full;
    @apply bg-orange-100 dark:bg-orange-500/15;
  }

  &__subtitle {
    @apply text-sm text-gray-500 dark:text-gray-400;
  }

  &__title {
    @apply mt-1 mb-5 text-xl font-bold text-gray-900 dark:text-white;
  }

  &__actions {
    @apply flex flex-row gap-3;
  }
}

// Анимация: затемнение проявляется, шторка выезжает снизу.
.start-prompt-enter-active,
.start-prompt-leave-active {
  transition: opacity 0.25s ease;

  .start-prompt__sheet {
    transition: transform 0.25s ease;
  }
}

.start-prompt-enter-from,
.start-prompt-leave-to {
  opacity: 0;

  .start-prompt__sheet {
    transform: translateY(100%);
  }
}
</style>
